import React from 'react';
import Joyride from 'react-joyride';
import { useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as ArrowNextIcon } from '@assets/svg/arrow-next.svg';
import { ReactComponent as CloseIcon } from '@assets/svg/cross.svg';

import useTranslation from '@hooks/useTranslation';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';

import { selectAppPersistOnBoardStart, useAppPersistActions } from '@store/useAppStorePersist';
import {
  selectUseOnboardStoreOnBoardStepIndex,
  selectUseOnboardStoreOnBoardStepsCounter,
  useOnboardStoreActions,
} from '@store/useOnboardStore';

import style from './style.module.scss';

const Onboarding = ({ steps }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { customerId } = useParams();
  const {
    setAppPersistOnBoardStart,
    setAppPersistOnBoardComplete,
    setAppPersistMapsVisible,
    setAppPersistSidebarResponse,
  } = useAppPersistActions();
  const { setUseOnboardStoreStepsCounter, setUseOnboardStoreAiId, setUseOnboardStoreStepIndex } =
    useOnboardStoreActions();
  const stepsCounter = selectUseOnboardStoreOnBoardStepsCounter();
  const stepIndex = selectUseOnboardStoreOnBoardStepIndex();
  const { data: assetTreeData } = useAssetTreeQuery();
  const onBoardStart = selectAppPersistOnBoardStart();

  const closeToolTip = () => {
    setAppPersistOnBoardStart(false);
    setAppPersistOnBoardComplete(true);
    setUseOnboardStoreStepsCounter(1);
    setUseOnboardStoreAiId(0);
    setUseOnboardStoreStepIndex(0);
    navigate('/');

    if (assetTreeData?.facilities.length > 1) {
      setAppPersistMapsVisible(true);
    }
  };

  const nextClick = () => {
    setUseOnboardStoreStepsCounter(stepsCounter + 1);
    setUseOnboardStoreStepIndex(stepIndex + 1);
  };

  const prevClick = () => {
    setUseOnboardStoreStepsCounter(stepsCounter - 1);
    setUseOnboardStoreStepIndex(stepIndex - 1);
  };

  const customTooltip = ({ continuous, step, backProps, closeProps, primaryProps, tooltipProps }) => {
    return (
      <div className={style.customTooltip} {...tooltipProps}>
        <div className={style.customTooltipClose} {...closeProps}>
          <CloseIcon onClick={closeToolTip} />
        </div>
        {step.title && <div className={style.customTooltipTitle}>{step.title}</div>}
        <div className={style.customTooltipContent}>
          <div className={style.customTooltipText}>{step.content}</div>
        </div>
        <div className={style.customTooltipFooter}>
          <div className={style.customTooltipCounter}>
            {stepsCounter} {t('of')} 20
          </div>
          <div className={style.customTooltipButtons}>
            {stepsCounter > 1 && (
              <div onClick={prevClick}>
                <div className={style.back} {...backProps}>
                  <ArrowNextIcon className={style.customTooltipArrow} />
                </div>
              </div>
            )}
            {continuous && (
              <div onClick={nextClick}>
                <div className={style.next} {...primaryProps}>
                  <ArrowNextIcon className={style.customTooltipArrow} />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleJoyrideCallback = (steps) => {
    if (steps.step?.data?.itemId) {
      setUseOnboardStoreAiId(steps.step?.data?.itemId || 'onboarding');
    }

    if (steps.step.target === '#tourStep-1') {
      setAppPersistMapsVisible(false);
      setAppPersistSidebarResponse(false);
    }

    if (steps.step.data?.navigateAfter) {
      navigate(`/customer/${customerId}/${steps.step.data?.navigateAfter}`);
      setUseOnboardStoreStepIndex(1);
    }

    if (steps.step.data?.navigateBefore && steps.action === 'prev' && steps.index === 0) {
      navigate(`/customer/${customerId}/${steps.step.data?.navigateBefore}`);
      setUseOnboardStoreStepIndex(steps.step.data?.previousItemIndex);
    }

    if (steps.step.data?.stepFinish || steps.action === 'reset') {
      navigate('/');
      setAppPersistOnBoardStart(false);
      setAppPersistOnBoardComplete(true);
      setUseOnboardStoreStepsCounter(1);
      setUseOnboardStoreAiId(0);
      setUseOnboardStoreStepIndex(0);

      if (assetTreeData?.facilities.length > 1) {
        setAppPersistMapsVisible(true);
      }
    }
  };

  if (!onBoardStart || !steps?.length) {
    return null;
  }

  return (
    <Joyride
      callback={handleJoyrideCallback}
      continuous
      tooltipComponent={customTooltip}
      disableOverlayClose
      scrollDuration={500}
      disableCloseOnEsc={true}
      steps={steps}
      disableScrollParentFix
      stepIndex={stepIndex}
    />
  );
};

export default Onboarding;
